import { SocialIcon } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { RetryOverlay } from './components/RetryOverlay';
import { ICON_VARIANTS } from './costants';
import { useSubComponentsFactory } from './factories/useSubComponentsFactory';
import { PostPreviewStyles } from './PostPreview.styles';
import type { PostPreviewProps } from './types';

export function PostPreview(props: PostPreviewProps) {
	const { variant, size, showPlatformIcon, disabled = false, onClick, retryState, onRetry } = props;
	const mainStyles = PostPreviewStyles({ size, disabled });

	// When in retry state, show loading skeleton with retry overlay
	const showRetryState = retryState === 'failed' || retryState === 'retrying';
	const effectiveProps = showRetryState ? { ...props, loading: true } : props;

	const { header, body, footer } = useSubComponentsFactory(effectiveProps);
	const iconConfig = ICON_VARIANTS[variant];

	const platformIconStyles = {
		xsmall: '-right-3 -top-3',
		small: '', // Not implemented, there are no use cases for small size with platform icon, nor designs.
		medium: '-right-[10px] -top-[10px]',
		large: '-right-[15px] -top-[15px]',
	};

	const platformIconSize = {
		xsmall: 'h-8 w-8',
		small: '', // Not implemented, there are no use cases for small size with platform icon, nor designs.
		medium: 'h-12 w-12',
		large: 'h-12 w-12',
	};

	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events
		<section
			data-testid={`post-preview-${variant}`}
			data-qa="post-preview-container"
			onClick={!disabled ? onClick : undefined}
			className={cn({ 'cursor-pointer': onClick && !disabled })}
		>
			{/*
				The platform icon is usually shown at the large size, but it is not constrained to that size.
				However, to support other sizes, add the appropriate styles for each size.
			*/}
			{showPlatformIcon && (
				<div className="relative z-10" aria-hidden>
					<div
						className={cn(
							'bg-background-inverse border-stroke-inverse-strong absolute flex items-center justify-center overflow-hidden rounded-full border',
							platformIconStyles[size],
							platformIconSize[size],
						)}
					>
						<SocialIcon
							data-testid="platform-icon"
							className={platformIconSize[size]}
							size={size === 'xsmall' ? 'medium' : 'large'}
							icon={<iconConfig.icon />}
							aria-hidden
							variant={iconConfig.variant}
						/>
					</div>
				</div>
			)}
			<div data-testid="preview-container" className={cn(mainStyles, 'relative')}>
				{header}
				{body}
				{footer}
				{showRetryState && <RetryOverlay size={size} retryState={retryState} variant={variant} onRetry={onRetry} />}
			</div>
		</section>
	);
}
